import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { downloadPdf } from "@/lib/api";
import { LEVELS } from "@/components/shared";
import ProofCard from "@/components/ProofCard";
import CapDossierSection from "@/components/CapDossierSection";
import { Loader2, FolderOpen, Check, Star, Download, TrendingUp } from "lucide-react";

const LEVEL_STYLE = {
  1: "bg-slate-200 text-slate-700 border-slate-300",
  2: "bg-sky-100 text-sky-800 border-sky-300",
  3: "bg-orange-100 text-orange-800 border-orange-300",
  4: "bg-orange-500 text-white border-orange-500",
};

const LEVEL_DOT = {
  1: "bg-slate-400",
  2: "bg-sky-500",
  3: "bg-orange-400",
  4: "bg-orange-600",
};

function LevelTimeline({ history, code }) {
  if (!history?.length) return null;
  return (
    <div className="mt-4 pt-3 border-t border-slate-100" data-testid={`timeline-${code}`}>
      <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
        <TrendingUp className="h-3.5 w-3.5" /> Évolution du niveau
      </p>
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {history.map((h, i) => (
          <div key={`${h.date}-${i}`} className="flex items-center shrink-0">
            {i > 0 && <div className="w-4 h-0.5 bg-slate-200" />}
            <div className="flex flex-col items-center gap-1" title={`${h.date} · ${LEVELS[h.level - 1]?.label}`}>
              <div className={`h-3 w-3 rounded-full ${LEVEL_DOT[h.level] || "bg-slate-300"}`} />
              <span className="text-[9px] font-bold text-slate-400 whitespace-nowrap">{h.date?.slice(5)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const [portfolio, setPortfolio] = useState(null);
  const [saving, setSaving] = useState(null);
  const [significatives, setSignificatives] = useState([]);
  const [exporting, setExporting] = useState(false);

  const loadSignif = useCallback(() => {
    api.get("/my-significatives").then(({ data }) => setSignificatives(data));
  }, []);

  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadPdf("/portfolio/pdf", "mon_portfolio.pdf");
      toast.success("Portfolio exporté en PDF ✓");
    } catch (e) {
      toast.error("Échec de l'export PDF. Réessaie.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get("/portfolio").then(({ data }) => setPortfolio(data));
    loadSignif();
  }, [loadSignif]);

  const setLevel = async (code, level) => {
    setSaving(code);
    try {
      await api.put(`/portfolio/${code}`, { level });
      setPortfolio((p) => p.map((c) => (c.code === code ? { ...c, level } : c)));
      toast.success(`${code} : ${LEVELS[level - 1].label} ✓`);
    } catch (e) {
      toast.error("Impossible d'enregistrer. Réessaie.");
    } finally {
      setSaving(null);
    }
  };

  if (!portfolio) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 text-orange-400 animate-spin" /></div>;

  const avg = (portfolio.reduce((a, c) => a + c.level, 0) / portfolio.length).toFixed(1);

  const BLOC_LABELS = {
    C2: "Bloc 2 · Mettre en valeur l'offre commerciale",
    C3: "Bloc 3 · Conseiller et accompagner le client",
  };
  const grouped = portfolio.reduce((acc, c) => {
    const k = c.code.slice(0, 2);
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});

  const tileClass = (n, active, reached) => {
    if (active) return `${LEVEL_STYLE[n]} scale-[1.02] shadow-sm`;
    if (reached) return "bg-orange-50 border-transparent text-orange-700";
    return "bg-slate-50 border-transparent text-slate-400 hover:bg-slate-100";
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 mz-rise">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-bold text-orange-500 uppercase tracking-wide">Suivi CCF · 4 niveaux</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1">Mon portfolio</h1>
          <p className="text-slate-500 font-medium mt-1">Positionne-toi sur chaque compétence, du Novice à l'Expert.</p>
        </div>
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm px-5 py-3 text-center">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Niveau moyen</p>
          <p className="font-display text-2xl font-semibold text-orange-600">{avg} / 4</p>
        </div>
      </div>

      <button
        onClick={exportPdf}
        disabled={exporting}
        data-testid="export-portfolio-pdf"
        className="mt-5 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white rounded-full px-5 py-2.5 font-bold text-sm transition-colors"
      >
        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {exporting ? "Génération…" : "Exporter mon portfolio (PDF)"}
      </button>

      {/* legend */}
      <div className="flex flex-wrap gap-2 mt-6">
        {LEVELS.map((l) => (
          <span key={l.n} className={`px-3 py-1 rounded-full text-xs font-bold border ${LEVEL_STYLE[l.n]}`}>{l.code} · {l.label}</span>
        ))}
      </div>

      {/* compétences significatives */}
      <div className="mt-8" data-testid="significatives-section">
        <h2 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Star className="h-5 w-5 fill-amber-400 text-amber-400" /> Compétences significatives
        </h2>
        <p className="text-slate-500 font-medium text-sm mb-3">Tes meilleures preuves, mises en avant pour ton dossier (Bloc 2 & Bloc 3).</p>
        {significatives.length === 0 ? (
          <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-200 p-6 text-center">
            <p className="text-sm text-slate-500 font-medium">Aucune preuve significative pour l'instant. Coche « significative » sur une preuve depuis tes cours.</p>
          </div>
        ) : (
          ["Bloc 2", "Bloc 3"].map((bloc) => {
            const list = significatives.filter((p) => p.bloc === bloc);
            if (list.length === 0) return null;
            return (
              <div key={bloc} className="mb-4">
                <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-2">{bloc}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {list.map((p) => <ProofCard key={p.id} proof={p} onChanged={loadSignif} />)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {Object.entries(grouped).map(([key, comps]) => (
        <div key={key} className="mt-8" data-testid={`portfolio-group-${key}`}>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">{BLOC_LABELS[key] || key}</h2>
          <div className="space-y-4">
            {comps.map((c) => (
              <div key={c.code} className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6" data-testid={`portfolio-${c.code}`}>
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                    <FolderOpen className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-lg font-semibold text-slate-900">{c.code} · {c.short}</p>
                    <p className="text-sm text-slate-500 font-medium">{c.name}</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {LEVELS.map((l) => {
                    const active = c.level === l.n;
                    const reached = c.level >= l.n;
                    return (
                      <button
                        key={l.n}
                        onClick={() => setLevel(c.code, l.n)}
                        disabled={saving === c.code}
                        data-testid={`set-${c.code}-${l.code}`}
                        className={`rounded-2xl border-2 px-2 py-3 text-center transition-all ${tileClass(l.n, active, reached)}`}
                      >
                        <span className="block text-xs font-extrabold">{l.code}</span>
                        <span className="block text-sm font-bold flex items-center justify-center gap-1">
                          {active && <Check className="h-3.5 w-3.5" />}{l.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <LevelTimeline history={c.history} code={c.code} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <CapDossierSection portfolio={portfolio} />
    </div>
  );
}
